/**
 * Auth Events - Event-based communication for authentication state changes
 *
 * This allows the API client to notify the AuthContext of auth failures
 * without creating circular dependencies.
 */

type AuthEventType = 'auth:logout' | 'auth:sessionExpired' | 'auth:accountDeactivated' | 'auth:error'

type AuthEventCallback = (event: { type: AuthEventType; message?: string }) => void

class AuthEventEmitter {
  private listeners: Map<AuthEventType, Set<AuthEventCallback>> = new Map()

  on(event: AuthEventType, callback: AuthEventCallback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(callback)

    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(callback)
    }
  }

  emit(event: AuthEventType, message?: string) {
    const callbacks = this.listeners.get(event)
    if (callbacks) {
      callbacks.forEach(callback => callback({ type: event, message }))
    }
  }

  // Clear all listeners (useful for cleanup)
  clear() {
    this.listeners.clear()
  }
}

export const authEvents = new AuthEventEmitter()
